import { useState, useRef, useEffect } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { appReleaseService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'

const MAX_BYTES = 250 * 1024 * 1024

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

const ReleaseCreatePage = () => {
  const navigate = useNavigate()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.APP_RELEASE,
    'write',
  )
  const fileInputRef = useRef(null)

  const [file, setFile] = useState(null)
  const [versionName, setVersionName] = useState('')
  const [versionCode, setVersionCode] = useState('')
  const [updateType, setUpdateType] = useState('SOFT')
  const [changelog, setChangelog] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [versionBounds, setVersionBounds] = useState(null)

  useEffect(() => {
    if (!canWrite) return undefined
    let cancelled = false
    appReleaseService
      .getVersionBounds()
      .then((bounds) => {
        if (cancelled) return
        setVersionBounds(bounds)
        if (!versionCode && bounds?.next_version_code) {
          setVersionCode(String(bounds.next_version_code))
        }
      })
      .catch(() => {
        if (!cancelled) setVersionBounds(null)
      })
    return () => {
      cancelled = true
    }
    // versionCode is seeded once from the bounds response
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canWrite])

  const validate = () => {
    const errs = {}
    if (!file) errs.file = 'APK file is required'
    else if (!file.name.toLowerCase().endsWith('.apk')) errs.file = 'Only .apk files are accepted'
    else if (file.size > MAX_BYTES) errs.file = `APK exceeds maximum size (${formatBytes(MAX_BYTES)})`

    if (!versionName.trim()) errs.versionName = 'Required'
    else if (versionName.length > 64) errs.versionName = 'Max 64 characters'

    const vc = parseInt(versionCode, 10)
    if (!Number.isFinite(vc) || vc < 1) errs.versionCode = 'Must be a positive integer'
    else if (
      versionBounds?.next_version_code != null &&
      vc < versionBounds.next_version_code
    ) {
      errs.versionCode = `Must be at least ${versionBounds.next_version_code}`
    }

    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleFile = (f) => {
    setFile(f)
    setFieldErrors((prev) => ({ ...prev, file: undefined }))
  }

  const handleSubmit = async () => {
    if (!canWrite) return
    setError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      const created = await appReleaseService.create({
        file,
        version_code: parseInt(versionCode, 10),
        version_name: versionName.trim(),
        update_type: updateType,
        changelog,
      })
      navigate(`/releases/${created.id}`)
    } catch (e) {
      const msg = e.response?.data?.message
      setError(Array.isArray(msg) ? msg.join('; ') : msg || e.message || 'Upload failed')
    } finally {
      setSubmitting(false)
    }
  }

  if (!canWrite) {
    return <Navigate to="/releases" replace />
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/releases')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Upload New Release
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            style={{ display: 'none' }}
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />

          {!file ? (
            <Box
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const dropped = e.dataTransfer.files?.[0]
                if (dropped) handleFile(dropped)
              }}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: '#fafafa',
                '&:hover': { borderColor: '#e90000', bgcolor: '#fff5f5' },
                mb: 3,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: '#999' }} />
              <Typography variant="h6" sx={{ mt: 1 }}>
                Drag &amp; drop APK here, or click to browse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Max {formatBytes(MAX_BYTES)} · .apk only
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#f5f7fa',
                borderRadius: 1,
                p: 2,
                mb: 3,
              }}
            >
              <CloudUploadIcon sx={{ mr: 2 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {file.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatBytes(file.size)}
                </Typography>
              </Box>
              <IconButton onClick={() => setFile(null)} disabled={submitting}>
                <CloseIcon />
              </IconButton>
            </Box>
          )}
          {fieldErrors.file && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {fieldErrors.file}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Version Name"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              placeholder="1.2.0"
              error={!!fieldErrors.versionName}
              helperText={fieldErrors.versionName || ' '}
              disabled={submitting}
              fullWidth
            />
            <TextField
              label="Version Code"
              type="number"
              value={versionCode}
              onChange={(e) => setVersionCode(e.target.value)}
              placeholder="120"
              error={!!fieldErrors.versionCode}
              helperText={
                fieldErrors.versionCode ||
                (versionBounds?.max_version_code != null
                  ? `Latest versionCode is ${versionBounds.max_version_code}; use ${versionBounds.next_version_code} or higher`
                  : versionBounds?.release_count === 0
                    ? 'First release — versionCode 1 or higher'
                    : 'Must be greater than the latest published versionCode')
              }
              disabled={submitting}
              fullWidth
            />
            <FormControl disabled={submitting}>
              <FormLabel sx={{ mb: 1 }}>Update Type</FormLabel>
              <RadioGroup row value={updateType} onChange={(e) => setUpdateType(e.target.value)}>
                <FormControlLabel value="SOFT" control={<Radio />} label="Soft" />
                <FormControlLabel value="FORCE" control={<Radio />} label="Force" />
              </RadioGroup>
            </FormControl>
          </Box>

          <TextField
            label="Changelog"
            value={changelog}
            onChange={(e) => setChangelog(e.target.value)}
            placeholder="- Fixed barcode scanner crash&#10;- Added Bengali language support"
            multiline
            rows={4}
            fullWidth
            disabled={submitting}
            sx={{ mt: 1 }}
          />

          <Alert severity="info" sx={{ mt: 3 }}>
            The uploaded release is <b>inactive</b> by default. Activate it on the next screen
            to push to mobile clients.
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button onClick={() => navigate('/releases')} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
              sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' }, minWidth: 180 }}
            >
              {submitting ? 'Uploading…' : 'Upload Release'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default ReleaseCreatePage
